import type { CashAccountBalance, CashCount, CashPosition } from '../api/cash';
import { formatAmount } from '../format/money';

/**
 * The pure half of the day-end count.
 *
 * <p>
 * <strong>Every rule here also exists on the server, and that is the point rather than a
 * duplication.</strong> The standing rule is never to offer a write the server will reject: a clerk
 * who has just counted a drawer should be told the reason is needed while the money is still in
 * their hand, not by a 400 after they press Save. What this must never do is compute the *variance
 * the record keeps* — that comes back from the server, which froze it.
 * </p>
 */

/** What the count said about the drawer. */
export type CountOutcome = 'agrees' | 'short' | 'over';

export function outcomeOf(variance: number): CountOutcome {
  if (variance === 0) {
    return 'agrees';
  }

  return variance < 0 ? 'short' : 'over';
}

/**
 * What each outcome is called.
 *
 * A `Record<CountOutcome, string>` rather than a lookup with a fallback, on the two-guard rule this
 * codebase applies to every wording map: a new outcome is a compile error here rather than a blank
 * cell, and `count.test.ts` asserts none of them is empty, since `''` satisfies the type and renders
 * nothing.
 */
export const outcomeLabels: Record<CountOutcome, string> = {
  agrees: 'Agrees with the ledger',
  short: 'Short',
  over: 'Over',
};

/**
 * The colour an outcome takes.
 *
 * <p>
 * <strong>`warning`, not `danger`, for both directions.</strong> The house meaning of amber is
 * needs-attention and of red is late — and a drawer that disagrees needs somebody to look at it
 * rather than being an emergency. Over is amber too: money nobody can account for is exactly as
 * much of a question as money missing, and colouring it green would congratulate a shop on a
 * receipt it never wrote up.
 * </p>
 */
export const outcomeTones: Record<CountOutcome, 'positive' | 'warning'> = {
  agrees: 'positive',
  short: 'warning',
  over: 'warning',
};

/**
 * Whether an explanation is compulsory — mirroring the server exactly.
 *
 * An adjustment with no explanation is a missing receipt filed as a fact, and the count row is the
 * only place that explanation can live: the ledger line it posts is a figure and a date.
 */
export function reasonRequired(variance: number): boolean {
  return variance !== 0;
}

/**
 * The accounts a shop can actually count.
 *
 * <p>
 * A bank balance is reconciled against a statement, line by line, which is a different mechanism and
 * a later slice — so the screen does not offer it. Filtering here rather than in the markup is what
 * lets the empty case below be answered honestly: a branch whose only account is a bank account has
 * nothing to count, which is not the same as having no accounts.
 * </p>
 */
export function countableAccounts(position: CashPosition | undefined): CashAccountBalance[] {
  return position?.accounts.filter((account) => account.kind !== 'Bank' && account.isActive) ?? [];
}

/**
 * Whether this account has already been counted on this day.
 *
 * <p>
 * The server refuses a second count with a 409 — the first has already settled its variance, so a
 * recount would reconcile the drawer to a figure that has since moved and post the difference twice.
 * Mirrored here so the button says so instead of the toast.
 * </p>
 */
export function alreadyCounted(
  counts: CashCount[] | undefined,
  accountId: number,
  day: string,
): boolean {
  return (counts ?? []).some(
    (count) => count.cashAccountId === accountId && count.countedOn.slice(0, 10) === day,
  );
}

/**
 * The one sentence standing between this form and Save, or null when nothing is.
 *
 * <p>
 * <strong>One at a time, most-blocking first</strong>, on the invoicing readiness notice's terms: a
 * list of four complaints under a form is read as a wall rather than as an instruction, and the
 * first one is the only one a person can act on anyway.
 * </p>
 */
export function countProblem(input: {
  counted: string;
  countedOn: string;
  today: string;
  notes: string;
  expected: number;
  alreadyCounted: boolean;
}): string | null {
  const { counted, countedOn, today, notes, expected } = input;

  if (countedOn === '') {
    return 'Say which day this is the count of.';
  }

  // Checked before the figure, matching the server's order, which checks the date first because an
  // absent date deserialises to 0001-01-01 and every rule that only looks forward passes on it.
  if (countedOn > today) {
    return 'A day that has not happened yet cannot be counted.';
  }

  if (input.alreadyCounted) {
    return 'This account has already been counted for that day.';
  }

  const value = Number(counted);

  if (counted.trim() === '' || !Number.isFinite(value)) {
    return 'Enter what was in the drawer — including a zero, if it was empty.';
  }

  if (value < 0) {
    return 'A drawer cannot hold less than nothing.';
  }

  if (reasonRequired(value - expected) && notes.trim() === '') {
    return 'The drawer does not agree with the ledger. Say what happened before settling the difference.';
  }

  return null;
}

/* ------------------------------------------------------------ note by note */

/**
 * The notes the phone counts one denomination at a time, largest first; everything smaller goes
 * in as coins, by its rupee value.
 *
 * <p>
 * <strong>Only the total ever reaches the server.</strong> `TblCashCount` records what was in the
 * drawer, not how it was made up, so this is arithmetic for the person holding the notes — and the
 * figure it produces goes through {@link countProblem} like one typed in whole on the website.
 * </p>
 */
export const drawerNotes = [500, 200, 100, 50, 20, 10] as const;

export type DrawerNote = (typeof drawerNotes)[number];

/** What has been typed against each note and the coins, as text, so a cleared box stays empty. */
export interface NoteCount {
  notes: Record<DrawerNote, string>;
  coins: string;
}

export function emptyNoteCount(): NoteCount {
  return {
    notes: { 500: '', 200: '', 100: '', 50: '', 20: '', 10: '' },
    coins: '',
  };
}

/** A box's typed text as a whole number, or null when it is empty or not one. */
function wholeOf(typed: string): number | null {
  const trimmed = typed.trim();

  return /^\d+$/.test(trimmed) ? Number(trimmed) : null;
}

/** What one row comes to — the number of notes times the note, or the coins as typed. */
export function noteValue(note: DrawerNote, typed: string): number {
  return (wholeOf(typed) ?? 0) * note;
}

/**
 * The drawer's total, or null while nothing has been typed.
 *
 * <p>
 * <strong>Null and zero are different answers</strong>, as on the website: an empty drawer is a
 * count, an untouched form is not. A box holding something that is not a whole number makes the
 * whole total null too, rather than being read as zero — the person can see the box, and a total
 * that quietly skipped a row is a drawer reconciled to the wrong figure.
 * </p>
 */
export function countedFromNotes(count: NoteCount): number | null {
  const typed = [...drawerNotes.map((note) => count.notes[note]), count.coins].filter(
    (text) => text.trim() !== '',
  );

  if (typed.length === 0 || typed.some((text) => wholeOf(text) === null)) {
    return null;
  }

  return (
    drawerNotes.reduce((sum, note) => sum + noteValue(note, count.notes[note]), 0) +
    (wholeOf(count.coins) ?? 0)
  );
}

/** The figure {@link countProblem} reads: what the notes add up to, or empty while they do not. */
export function countedText(count: NoteCount): string {
  const total = countedFromNotes(count);

  return total === null ? '' : String(total);
}

/**
 * What the count says against the books, said before Save — the website's notice, in the phone's
 * words. Null when there is nothing typed yet.
 *
 * <p>
 * <strong>Saving settles</strong>: the server posts the difference as an adjustment, so the balance
 * afterwards is what was counted. That is the part a person is agreeing to, which is why it is in
 * the sentence rather than left to be discovered.
 * </p>
 */
export function countVerdict(
  counted: number | null,
  expected: number,
  currency = '',
): { outcome: CountOutcome; headline: string; detail: string } | null {
  if (counted === null) {
    return null;
  }

  const variance = counted - expected;
  const outcome = outcomeOf(variance);
  const books = `${currency}${formatAmount(expected)}`;

  if (outcome === 'agrees') {
    return { outcome, headline: 'Agrees with the books', detail: `The books say ${books} too.` };
  }

  return {
    outcome,
    headline: `${currency}${formatAmount(Math.abs(variance))} ${outcome}`,
    detail: `of what the books say (${books}). Saving sets the drawer to what you counted.`,
  };
}

/** The save button, with the figure it records on it. */
export function saveCountLabel(counted: number | null, currency = ''): string {
  return counted === null
    ? 'Count the notes first'
    : `Save count · ${currency}${formatAmount(counted)}`;
}
